export const goalsEnTranslations = {
  // Page
  pageTitle: 'Goals',

  // Tasks
  createTaskButton: 'Create Task',
  createGoalTitle: 'Create New Goal',
  createTask: 'Create Task',

  // Create Goal Modal
  createGoalButton: 'Create Goal',
  createChildGoalTitle: 'Create Child Goal',
  addNestedGoal: 'Add Subgoal',
  addNestedTask: 'Add Task',
  addNestedItem: 'Add',
  addNestedGoalTooltip: 'Create a nested goal under this one',
  addNestedItemTooltip: 'Create a subgoal or task under this goal',
  createTaskTitle: 'Create New Task',

  // Update Goal Modal
  updateGoalTitle: 'Update Goal',
  updating: 'Updating...',
  updateGoal: 'Update Goal',

  // Update Task Modal
  updateTaskTitle: 'Update Task',
  updateTask: 'Update Task',

  // View Goal Modal
  viewGoalTitle: 'Goal Details',
  viewTaskTitle: 'Task Details',

  // Delete Goal
  deleteGoal: {
    title: 'Delete Goal',
    description: (goalTitle: string) =>
      `Are you sure you want to delete "${goalTitle}" and all its descendants? This action cannot be undone.`,
    confirm: 'Delete',
    cancel: 'Cancel',
    deleting: 'Deleting...',
  },

  // Goal Form
  title: 'Title',
  description: 'Description (optional)',
  projectId: 'Project Id',
  status: 'Status',
  startDate: 'Start Date',
  dueDate: 'Due Date',
  cancel: 'Cancel',
  creating: 'Creating...',
  createGoal: 'Create Goal',
  enrichGoal: 'Enrich With AI',
  enriching: 'Enriching...',
  relevantSignals: 'Relevant Signals Learned',
  loadingSignalSuggestions: 'Finding relevant signals...',
  suggestedSignalsDescription:
    'Suggested based on title and description. Uncheck to exclude from task.',
  includeSignalWithTask: 'Include this signal with task',
  fromInteraction: 'From Interaction',

  // View Goal Labels
  titleLabel: 'Title',
  descriptionLabel: 'Description',
  statusLabel: 'Status',
  assigneesLabel: 'Assignees',
  collaboratorsLabel: 'Collaborators',
  parentGoalLabel: 'Parent Goal',
  startDateLabel: 'Start Date',
  dueDateLabel: 'Due Date',
  linkLabel: 'Link',
  creatorLabel: 'Created By',
  ownerLabel: 'Owner',
  selectOwner: 'Select Owner',
  selectCreator: 'Select Creator',
  complete: 'complete',

  // Goals Tree View
  loadingGoals: 'Loading goals...',
  errorLoadingGoals: (error: string) => `Error loading goals: ${error}`,
  noGoalsFound: 'No goals found',
  showCompleted: 'Show Completed Goals and Tasks',
  filters: 'Filters',
  filtersDescription: 'Filter goals and tasks by employee or completion status',
  filterByEmployee: 'Filter by Employee',

  // Goals Tree Header
  expandAll: 'Expand All',
  collapseAll: 'Collapse All',

  // Key Results
  keyResultsLabel: "KPI's (Optional)",
  keyResultsDescription: 'Define measurable outcomes for this goal',
  addKeyResult: 'Add Key Result',
  addFirstKeyResult: 'Add First Key Result',
  keyResult: 'Key Result',
  keyResultType: 'Metric Type',
  selectKeyResultType: 'Select type',
  maximumType: 'Goal Target (higher value is better)',
  minimumType: 'Upper Limit (lower value is better)',
  targetQuantity: 'Target Quantity',
  targetQuantityPlaceholder: 'e.g., 1000',
  unit: 'Unit',
  unitPlaceholder: 'e.g., users, clicks, %',
  currentProgress: 'Current Progress',
  keyResultTitlePlaceholder: 'e.g., Increase user engagement',
  noKeyResults: 'No key results yet',
  noKeyResultsDescription:
    'Add measurable outcomes to track your goal progress',
  repeatableKeyResult: 'Repeatable Key Result',
  recurrencePattern: 'Recurrence Pattern',
  selectRecurrencePattern: 'Select pattern',
  recurrenceDaily: 'Daily',
  recurrenceWeekly: 'Weekly (every 7 days)',
  recurrenceMonthly: 'Monthly',
  recurrenceCustom: 'Custom Interval',
  intervalDays: 'Repeat Every (days)',
  viewHistory: 'View History',
  occurrenceHistory: 'Occurrence History',
  currentOccurrence: 'Current Occurrence',
  pastOccurrences: 'Past Occurrences',
  currentBadge: 'Current',
  started: 'Started',
  progress: 'Progress',
  period: 'Period',
  completed: 'Completed',
  noHistory: 'No history yet',
  noHistoryDescription:
    'History will appear when there will be finished occurrences',

  // Repetitive Tasks
  repeatableTask: 'Repeatable Task',
  repeatableTaskInfo:
    'The task will automatically create new occurrences based on the pattern until the task due date',
  yes: 'Yes',
  no: 'No',
  taskOccurrenceHistory: 'Task Occurrence History',
  recurrenceStartDate: 'Recurrence Start Date',
  recurrenceEnd: 'Recurrence End',
  noEndDate: 'No end date',
  endAfterOccurrences: 'End after X occurrences',
  repeatUntilDate: 'Repeat until a specific date',
  numberOfOccurrences: 'Number of Occurrences',
  endDate: 'End Date',
  closeEntireTask: 'Entire Task Finished',
  closing: 'Closing...',
  taskHasClosed:
    'This task has been closed - no new occurrences will be generated',
  reopenTask: 'Reopen Task',
  reopening: 'Reopening...',

  // Recurring task completion
  occurrenceCompleted: 'Occurrence Completed',
  occurrenceCompletedDescription: (nextDate: string) =>
    `This occurrence is done. Next occurrence starts ${nextDate}.`,
  nextOccurrenceStarts: (date: string) => `Next: ${date}`,
  occurrenceMarkedComplete: 'Marked as completed',
  occurrenceMarkedIncomplete: 'Marked as incomplete',
  deleteOccurrence: 'Delete',
  deleteOccurrenceSuccess: 'Occurrence deleted',

  // Stop recurrence
  stopRecurrence: 'Stop All Future Occurrences',
  stopRecurrenceWarning:
    'This will permanently stop the recurrence. No new occurrences will be created.',
  recurrencePermanentlyStopped: 'Recurrence has been permanently stopped.',

  // Recurrence patterns
  repeatsDaily: 'Repeats daily',
  repeatsWeekly: 'Repeats weekly',
  repeatsMonthly: 'Repeats monthly',
  repeatsCustom: (days: number) => `Repeats every ${days} days`,

  // Recurrence badge labels (short, for inline badge)
  badgeDaily: 'Daily',
  badgeWeekly: 'Weekly',
  badgeMonthly: 'Monthly',
  badgeCustom: 'Custom',

  // Recurrence metadata line
  dueLabel: (date: string) => `Due: ${date}`,
  currentDone: 'Done',
  nextLabel: (date: string) => `Next: ${date}`,

  // Parent Goal Selector
  selectParentGoal: 'Select a parent goal',
  noParentGoal: 'No parent goal',

  // Status
  statusCompleted: 'Completed',
  statusInProgress: 'In Progress',
  statusTodo: 'To Do',
  statusUnknown: 'Unknown',
  failedToUpdateStatus: 'Failed to Update Status',
  couldNotUpdateGoalStatus: 'Could not update goal status. Please try again.',

  // Goal Card
  fromSubGoals: (count: number) => `• ${count} from sub-goals`,
  fromSubGoalsLabel: (count: number) => `${count} from sub-goals`,
  interactionsLabel: 'Interactions',
  blockagesLabel: (count: number) =>
    count === 1 ? `${count} blockage` : `${count} blockages`,
  goalTypeLabel: 'Parent Goal',
  taskTypeLabel: 'Task',

  // Empty State
  createFirstGoal: 'Create your first goal to get started',

  // AI Enrichment
  noRelevantSignals: 'No relevant signals found',

  // Interactions
  interactions: {
    title: 'Goal Interactions',
    description: (goalTitle: string) =>
      `All interactions related to "${goalTitle}"`,
    loading: 'Loading interactions...',
    empty: 'No interactions found for this goal',
    sender: 'From',
    receiver: 'To',
    status: 'Status',
    coreParticipants: 'Core Participants',
    otherParticipants: 'Other Participants',
    interactionTypeISeeYou: 'I See You',
    interactionTypeINeedYou: 'I Need You',
    interactionTypeILearn: 'I Learn',
    blockage: 'Blockage',
    goalLabel: 'Goal:',
  },

  // Tasks Table
  tasksTable: {
    headerStatus: 'Status',
    headerTask: 'Task',
    headerAssignees: 'Assignees',
    headerStartDate: 'Start Date',
    headerDueDate: 'Due Date',
    headerFinishDate: 'Finish Date',
    headerActions: 'Actions',
    headerInteractions: 'Interactions',
    noTasksYet: 'No tasks yet',
    taskCount: (count: number) =>
      count === 1 ? `${count} task` : `${count} tasks`,
    completed: 'completed',
    inProgress: 'in progress',
    percentComplete: (percent: number) => `${percent}% complete`,
  },

  // KPI Progress Update
  kpi: {
    updateProgress: 'Update Progress',
    setProgressFor: (title: string) => `Set progress for ${title}`,
    updateProgressButton: 'Update Progress',
    updating: 'Updating...',
    progressUpdated: 'Progress updated',
    progressUpdatedDescription: (
      title: string,
      progress: number,
      unit: string
    ) => `${title} progress updated to ${progress} ${unit}`,
    invalidValue: 'Invalid value',
    invalidValueDescription: 'Please enter a valid positive number',
    cannotBeNegative: 'Progress cannot be negative',
    updateFailed: 'Update failed',
    updateFailedDescription: 'Failed to update KPI progress. Please try again.',
    maxValue: 'Max',
  },

  // Import
  import: 'Import',
  importSources: 'Import Sources',
  selectImportSource: 'Choose a source to import goals and tasks from',
  msProjectTitle: 'Microsoft Project',
  importFromMsProject: 'Import from XML file',

  // MS Project Import
  importMsProject: 'Import from MS Project',
  importing: 'Importing...',
  importSuccess: 'Import Successful',
  importSuccessDescription:
    'Your MS Project file has been imported successfully.',
  goalsCreated: 'Goals Created',
  tasksCreated: 'Tasks Created',
  totalItems: 'Total Items',
  close: 'Close',
  importError: {
    title: 'Import Failed',
    generic: 'Failed to import MS Project file. Please try again.',
    invalidFileType: 'Please select a valid XML file from Microsoft Project.',
  },

  // Excel Export
  exportToExcel: 'Export to Excel',
  exporting: 'Exporting...',
  excelColumnIdx: 'No.',
  excelColumnTitle: 'Title',
  excelColumnOwner: 'Owner',
  excelColumnAssignees: 'Assignees',
  excelColumnStartTime: 'Start Time',
  excelColumnEndTime: 'End Time',
  excelColumnDuration: 'Duration',
  excelColumnActualEndDate: 'Actual End Date',
  excelColumnRemarks: 'Remarks',
  excelHeaderCreator: 'Creator',
  excelHeaderExportDate: 'Export Date',
  excelHeaderGoalDueDate: 'Goal Due Date',
  // Restricted Goal
  restrictedGoal: {
    title: 'Private Goal',
    description: 'You do not have permission to view this goal',
  },

  // Viewers
  viewers: 'Viewers',
  selectViewers: 'Select viewers',
  searchEmployees: 'Search employees...',
  noEmployeesFound: 'No employees found',
  selectionCount: (count: number) =>
    count === 1 ? `${count} viewer selected` : `${count} viewers selected`,

  // Privacy
  privacy: 'Privacy',
  private: 'Private',
  public: 'Public',
  publicCompanyWide: 'Public (company-wide)',
  visibleToAllInCompany: 'Visible to all users in your company',
  publicDescription: 'Make this goal visible to the entire company',
  privateDescription: 'Only show this goal to its owner and members',
  editingSingleOccurrence:
    'Editing this occurrence only. The recurring series will continue unchanged.',

  // Integrations Dialog
  integrationsDialog: {
    title: 'Integration Center',
    description: 'Connect and manage your project tools',
    activeConnections: 'Active Connections',
    addIntegration: 'Add Integration',
    connected: 'Connected',
    noOtherIntegrations: 'No integrations connected',
    noOtherIntegrationsDescription:
      'Connect your project management tools to sync issues and goals.',
    browseIntegrations: 'Browse Integrations',
    connectMoreTools: 'Connect more tools',
    searchPlaceholder: 'Search integrations...',
    jira: 'Jira',
    jiraDescription: 'Sync issues and epics',
    linear: 'Linear',
    linearDescription: 'Track issues and projects',
    monday: 'Monday',
    mondayDescription: 'Manage work and projects',
    salesforce: 'Salesforce',
    salesforceDescription: 'Align CRM with goals',
    bamboohr: 'BambooHR',
    bamboohrDescription: 'Sync employee data',
    sharepoint: 'SharePoint',
    sharepointDescription: 'Access and sync SharePoint documents',
    dynamicsCrm: 'Dynamics CRM',
    dynamicsCrmDescription: 'Sync CRM data with goals',
    confluence: 'Confluence',
    confluenceDescription: 'Access and sync Confluence pages',
    crmConnectTitle: 'Connect Dynamics CRM',
    crmConnectDescription:
      'Enter your Dynamics CRM organization URL to connect your instance.',
    crmUrlPlaceholder: 'yourorg.crm4',
    crmUrlHelper:
      'Enter your org name and region (e.g. yourorg.crm, yourorg.crm4).',
    crmUrlPrefix: 'https://',
    crmUrlSuffix: '.dynamics.com',
    continue: 'Continue',
    syncPlansAndTimelines: 'Sync plans and timelines',
    installed: 'Installed',
    comingSoon: 'Coming Soon',
    disconnect: 'Disconnect',
    disconnecting: 'Disconnecting...',
    cancel: 'Cancel',
    aiConnections: 'AI Tools',
    aiConnectionsDescription:
      'Connect tools the AI can use when analyzing signal relevance',
    aiConnect: 'Connect',
    aiConnected: 'Connected',
    aiToolkits: {
      jira: {
        name: 'Jira (AI)',
        description:
          'Let AI search related Jira issues for better signal matching',
      },
      share_point: {
        name: 'SharePoint (AI)',
        description:
          'Let AI search SharePoint files and content for better signal matching',
      },
      confluence: {
        name: 'Confluence (AI)',
        description:
          'Let AI search Confluence pages and content for better signal matching',
      },
    },
  },

  // Integrations
  integrations: {
    connectedIntegrations: 'Connected Integrations',
    manageAndSync: 'Manage your project integrations',
    addAnotherIntegration: 'Add Another Integration',
    noIntegrations: 'No Integrations',
    noIntegrationsDescription:
      'Connect Jira or other project management tools to sync your issues and goals.',
    connectIntegration: 'Connect Integration',
    connecting: 'Connecting...',
    integrations: 'Integrations',
    syncIssues: 'Sync Issues',
    syncing: 'Syncing...',
    syncSuccess: (totalIssues: number, provider: string) =>
      `Synced ${totalIssues} issues from ${provider} successfully!`,
    syncFailed: (provider: string) => `Failed to sync ${provider} issues`,
    disconnectConfirm: (provider: string) =>
      `Are you sure you want to disconnect ${provider} integration?`,
    disconnectSuccess: (provider: string) =>
      `${provider} integration disconnected`,
    disconnectFailed: (provider: string) =>
      `Failed to disconnect ${provider} integration`,
    connectWebhook: 'Connect Webhook',
    reconnectWebhook: 'Reconnect Webhook',
    registeringWebhook: 'Registering webhook...',
    webhookRegistered: 'Webhook registered successfully',
    webhookFailed: 'Failed to register webhook',
  },
};
